import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ModalBaseComponent } from 'app/shared/components/base/modal-base/modal-base.component';
import { ModuleSelectionComponent } from 'app/shared/components/module-selection/module-selection.component';
import { Permissions } from 'app/shared/constants/permissions';
import { Sections } from 'app/shared/constants/sections';
import { CompanyBase } from 'app/shared/models/company-base.model';
import { ModuleSelection } from 'app/shared/models/module-selection.model';
import { Role } from 'app/shared/models/role.model';
import { Section } from 'app/shared/models/section.model';
import { UserGroup } from 'app/shared/models/user-group.model';
import { UserModule } from 'app/shared/models/user-module.model';
import { UserRole } from 'app/shared/models/user-role.model';
import { User } from 'app/shared/models/user.model';
import { SectionService } from 'app/shared/services/section.service';
import { UserService } from 'app/shared/services/user.service';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})

export class UserComponent extends ModalBaseComponent implements OnInit {

  @ViewChild(ModuleSelectionComponent, {static: true}) moduleSelectionComponent;

  sections: Array<Section>;
  form: FormGroup;

  permissionsLoaded = false;

  get formSections(): FormArray {
    return this.form.get('formSections') as FormArray;
  }

  get hasSelectCompanyPermission(): boolean {
    return this.hasPermission(Permissions.User_SelectCompany);
  }

  get hasSelectUserGroupPermission(): boolean {
    return this.hasPermission(Permissions.User_SelectUserGroup);
  }

  constructor(public userService: UserService, public sectionService: SectionService, protected activeModal: NgbActiveModal, private formBuilder: FormBuilder,
              protected translateService: TranslateService) {
    super(activeModal, sectionService, Sections.User);

    this.form = this.formBuilder.group({
      userId: [''],
      userName: [''],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: [''],
      function: ['', [Validators.required]],
      twoFactorSetupEnabled: [''],
      twoFactorEnabled: [''],
      companyId: ['', [Validators.required]],
      userGroupCode: [''],
      formSections: new FormArray([]),
    });
  }

  ngOnInit() {
    this.initializeObject();
    this.translateService.get(['user.addedSuccessfullyMessage', 'user.updatedSuccessfullyMessage'])
      .subscribe(translations => {
        this.addedSuccessfullyMessage = translations['user.addedSuccessfullyMessage'];
        this.updatedSuccessfullyMessage = translations['user.updatedSuccessfullyMessage'];
      });
  }

  initializeObject() {
    if (this.currentObject == null) {
      this.currentObject = new User();

      // Get the company of the current api user in case the company selector is disabled
      this.userService.getObject<CompanyBase>('/users/company', null, (companyBase) => {
        if (companyBase) {
          this.currentObject.CompanyId = companyBase.CompanyId;
          this.form.patchValue({
            companyId: this.currentObject.CompanyId,
          });
        }
      });
    }

    this.updateFormFieldValuesWithCurrentObject();
    super.initializeObject();
  }

  resetAdditionalFields() {
    super.resetAdditionalFields();
    this.updateFormFieldValuesWithCurrentObject();
  }

  handleUserGroupChanged(userGroup: UserGroup) {
    if (userGroup) {
      this.currentObject.UserGroupCode = userGroup.Code;
    } else {
      this.currentObject.UserGroupCode = undefined;
    }

    this.form.patchValue({
      userGroupCode: this.currentObject.UserGroupCode,
    });
  }

  handleCompanyChanged(company: CompanyBase) {
    if (company) {
      this.currentObject.Company = company;
      this.currentObject.CompanyId = company.CompanyId;
    } else {
      this.currentObject.Company = undefined;
      this.currentObject.CompanyId = undefined;
    }

    this.form.patchValue({
      companyId: this.currentObject.CompanyId,
    });
  }

  onSubmit() {
    if (this.currentObject.UserId && !this.permissionsLoaded) {
      return;
    }

    if (this.moduleSelectionComponent.hasInactiveSelectedModules()) {
      return;
    }

    this.updateCurrentObjectWithFormFieldValues();

    if (!this.form.value.userId) {
      this.userService.postUser(this.currentObject, () => this.handleSubmit(this.form), this.addedSuccessfullyMessage);
    } else {
      this.userService.putUser(this.form.value.userId, this.currentObject, () => this.handleSubmit(this.form), this.updatedSuccessfullyMessage);
    }
  }

  private updateFormFieldValuesWithCurrentObject() {
    this.form.patchValue({
      userId: this.currentObject.UserId,
      userName: this.currentObject.UserName,
      firstName: this.currentObject.FirstName,
      lastName: this.currentObject.LastName,
      email: this.currentObject.Email,
      phoneNumber: this.currentObject.PhoneNumber,
      function: this.currentObject.Function,
      twoFactorSetupEnabled: this.currentObject.TwoFactorSetupEnabled,
      twoFactorEnabled: this.currentObject.TwoFactorEnabled,
      companyId: this.currentObject.CompanyId,
      userGroupCode: this.currentObject.UserGroupCode
    })

    this.updateFormSections();
  }

  private updateFormSections() {
    if (this.currentObject.UserId) {
      this.sectionService.getCollection<Section>('/section/permissions/user/' + this.currentObject.UserId, null, (collection) => {
        this.sections = collection;
        this.addSectionsToForm();
        this.permissionsLoaded = true;
      });
    }
  }

  private updateCurrentObjectWithFormFieldValues() {
    this.currentObject.UserId = this.form.value.userId;
    this.currentObject.UserName = this.form.value.userName;
    this.currentObject.FirstName = this.form.value.firstName;
    this.currentObject.LastName = this.form.value.lastName;
    this.currentObject.Email = this.form.value.email;
    this.currentObject.PhoneNumber = this.form.value.phoneNumber;
    this.currentObject.Function = this.form.value.function;
    this.currentObject.TwoFactorSetupEnabled = this.form.value.twoFactorSetupEnabled;
    this.currentObject.TwoFactorEnabled = this.form.value.twoFactorEnabled;
    this.currentObject.CompanyId = this.form.value.companyId;
    this.currentObject.UserGroupCode = this.form.value.userGroupCode;

    this.applySelectedModulesToUser();
    this.addSelectedPermissionsToUserRoles();
  }

  private applySelectedModulesToUser() {
    this.currentObject.UserModules.length = 0;
    var selectedModules: Array<ModuleSelection> = this.moduleSelectionComponent.getSelected();

    for (let selectedModule of selectedModules) {
      if (selectedModule.IsChecked) {
        this.currentObject.UserModules.push(new UserModule(this.currentObject.UserId, selectedModule.ModuleId));
      }
    }
  }

  private addSelectedPermissionsToUserRoles() {
    this.currentObject.Roles.length = 0;
    // Add roles to the user
    for (let formSection of this.form.value.formSections) {
      for (let formRole of formSection.formRoles) {
        if (formRole.IsChecked) {
          this.currentObject.Roles.push(new UserRole(this.currentObject.UserId, formRole.RoleId));
        }
      }
    }
  }

  private addSectionsToForm() {
    this.formSections.clear();
    this.sections.forEach(section => {
      var sectionFields = this.buildSectionFields(section);
      this.formSections.push(sectionFields);
    });
  }

  private buildSectionFields(section: Section) {
    var sectionFields = this.formBuilder.group({
      SectionId: [section.SectionId],
      Name: [section.Name],
      formRoles: new FormArray([])
    });

    this.addRolesToForm(section, sectionFields)

    return sectionFields;
  }

  private addRolesToForm(section: Section, sectionFields: FormGroup) {
    section.Roles.forEach(role => {
      var roleFields = this.buildRoleFields(role);
      (sectionFields.controls.formRoles as FormArray).push(roleFields);
    });
  }

  private buildRoleFields(role: Role) {
    return this.formBuilder.group({
      RoleId: [role.RoleId],
      Name: [role.Name],
      IsChecked: [role.IsChecked]
    });
  }
}
